import { makeAutoObservable, runInAction } from 'mobx'

// evertel
import { decorate, inject, injectable } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { BlueUserStore } from '@evertel/blue-user'
import { Api } from '@evertel/api'
import { APIDataDocumentSchema } from '@evertel/types'

const version = 1
const MAX_RECENT_TEMPLATES = 20

class DocumentRecentTemplatesController {
    constructor(
        private session: SessionService,
        private userStore: BlueUserStore,
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    get recentSchemaIds(): number[] {
        const user = this.userStore.findById(this.session.currentUserId)

        const stored = user?.meta?.recentTemplates
        return stored?.version === version ? stored.schemaIds ?? [] : []
    }

    // pool is the caller's already department-scoped schema list, so ids from other
    // departments (or disabled/deleted schemas) drop out naturally without per-department storage
    getRecentSchemas = (pool: APIDataDocumentSchema[], top = 8): APIDataDocumentSchema[] => {
        return this.recentSchemaIds
            .map(id => pool.find(schema => schema.id === id))
            .filter((schema): schema is APIDataDocumentSchema => !!schema)
            .slice(0, top)
    }

    recordTemplateUsed = async (schemaId: number) => {
        if (!schemaId) return

        const updatedIds = [schemaId, ...this.recentSchemaIds.filter(id => id !== schemaId)]
            .slice(0, MAX_RECENT_TEMPLATES)

        // the generated route type asks for the full user, but the endpoint accepts a
        // partial and merges `meta` (same usage as CurrentUserEmojisController)
        const userData = await this.api.Routes.BlueUser.putById(this.session.currentUserId, {
            meta: {
                recentTemplates: {
                    schemaIds: updatedIds,
                    version
                }
            }
        } as any)

        runInAction(() => {
            this.userStore.update(userData)
        })
    }
}

decorate(injectable(), DocumentRecentTemplatesController)
decorate(inject(SessionService), DocumentRecentTemplatesController, 0)
decorate(inject(BlueUserStore), DocumentRecentTemplatesController, 1)
decorate(inject(Api), DocumentRecentTemplatesController, 2)

export { DocumentRecentTemplatesController }
